import React, {useContext, useEffect, useRef, useState} from "react";
import Button from "../Button/Button";
import {LazyLoadImage} from "react-lazy-load-image-component";
import {
	InfiosHeader,
	InfiosHeaderContainer,
	InfiosHeaderLogo,
	InfiosHeaderMetaWrapper,
	InfiosHeaderWrapper,
} from "./Header.style";
import LanguageSelector from "../LanguageSelector/LanguageSelector";
import SearchNavigation from "../SearchNavigation/SearchNavigation";
import HeaderMetaNav from "./HeaderMetaNav/HeaderMetaNav";
import HeaderNavigation from "./HeaderNavigation/HeaderNavigation";
import {colors, sizes} from "../../utils/styled/_variables.styled";
import ContactFormOverlay from "../ContactFormOverlay/ContactFormOverlay";
import {StateContext} from "../../context/stateContext";
import {ErrorBoundary} from "react-error-boundary";
import {urlHasParam} from "../../utils/js/params";
import targetView from "../../utils/targetView";
import {debounce} from "../../utils/debounce";

const Header = (props) => {
	const {
		titleLogo,
		altLogo,
		hideLogo,
		logoURL,
		darkLogoImage,
		lightLogoImage,
		logoTargetLink,
		blackLetter,
		buttonText,
		metaNavigationLinks,
		navigationLinks,
		showSearch,
		showLanguageNavigation,
		isInEditor,
		backToText,
		overviewText,
		captchaConfig,
		pageLanguage,
		resultsPage,
		hoverColor,
		contactUrl,
		contactUrlTarget,
		contactButtonColor,
		contactButtonTextColor,
		contactButtonHoverColor,
		contactButtonTextHoverColor,
		formBaseColor
	} = props;

	const {setScrolled, isOverlayFormOpen, setIsOverlayFormOpen} = useContext(StateContext);

	const containerRef = useRef(null);
	const navRef = useRef(null);
	const headerRef = useRef(null);

	const [isCollapsed, setIsCollapsed] = useState(false);
	const [scrollPosition, setScrollPosition] = useState(0);
	const [lastScrollPosition, setLastScrollPosition] = useState(0);
	const [mouseLeave, setMouseLeave] = useState(false);
	const [height, setHeight] = useState(0);
	const [status, setStatus] = useState(false);
	const scrollWidthRef = useRef(0);
	const [isMobile, setIsMobile] = useState(false);

	const [logoSrc, setLogoSrc] = useState((blackLetter === "whiteBlack" || blackLetter === "transparentBlack") ? darkLogoImage : lightLogoImage);

	useEffect(() => {
		const updateScrollPadding = () => {
			if (containerRef.current) {
				const headerHeight = containerRef.current.offsetHeight;

				const styleTag = document.createElement("style");
				styleTag.id = "dynamic-scroll-padding";
				styleTag.innerHTML = `
                html {
                    scroll-behavior: smooth;
                    scroll-padding-top: ${headerHeight}px;
                }
            `;
				const existingStyle = document.getElementById("dynamic-scroll-padding");
				if (existingStyle) {
					existingStyle.remove();
				}

				document.head.appendChild(styleTag);
			}
		};

		updateScrollPadding();

		window.addEventListener("resize", updateScrollPadding);

		return () => {
			window.removeEventListener("resize", updateScrollPadding);
			const styleTag = document.getElementById("dynamic-scroll-padding");
			if (styleTag) styleTag.remove();
		};
	}, []);

	useEffect(() => {
		const handleScroll = () => {
			setScrollPosition(window.pageYOffset);
		};

		if (sessionStorage.getItem('openCFO')) {
			handleOpenForms(true);
		}

		window.addEventListener("scroll", handleScroll);

		setHeight(containerRef.current.offsetHeight);

		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, []);

	useEffect(() => {
		setIsCollapsed(scrollPosition > lastScrollPosition);
		setScrolled(scrollPosition > lastScrollPosition);
		setHeight(scrollPosition > lastScrollPosition ? 121 : 130);

		setLastScrollPosition(scrollPosition);

		if (mouseLeave === "") return;
		setLogoSrc((blackLetter === "whiteBlack" || blackLetter === "transparentBlack") || scrollPosition > 5 ? darkLogoImage : lightLogoImage);
	}, [scrollPosition]);

	const handleMouseLeave = (mouseState) => {
		setMouseLeave(mouseState);
		setLogoSrc((blackLetter === "whiteBlack" || blackLetter === "transparentBlack") || mouseState === "" || scrollPosition > 5 ? darkLogoImage : lightLogoImage);
	};

	const handleStatus = (status) => {
		setStatus(status);
		setLogoSrc((blackLetter === "whiteBlack" || blackLetter === "transparentBlack") || status || scrollPosition > 5 ? darkLogoImage : lightLogoImage);
	}

	const handleOpenForms = (open) => {
		setIsOverlayFormOpen(open);
		if (open) {
			targetView("header-forms-" + open);
		}
	}

	useEffect(() => {
		if (containerRef.current) {
			containerRef.current.parentElement.classList.add(`unset__grid`);
			containerRef.current.style.opacity = "100%";
		}

	}, []);

	useEffect(() => {
		function checkMenuFit() {
			if (navRef.current) {
				const navMenu = navRef.current;
				const navSpaceSize = headerRef.current.offsetWidth - 160; //160 is the logo width
				const mobileScreen = window.innerWidth < sizes.tabletLarge

				let scrollWidth = scrollWidthRef.current;
				if(navMenu.scrollWidth >  scrollWidthRef.current){
					scrollWidthRef.current = navMenu.scrollWidth;
					scrollWidth = navMenu.scrollWidth;
				}

				setIsMobile(mobileScreen || (scrollWidth >= navSpaceSize));
				navMenu.parentElement.style.opacity = "100%";
			}
		}

		setTimeout(checkMenuFit,0);

		const debouncedCheck = debounce(checkMenuFit, 100);
		window.addEventListener('resize', checkMenuFit);

		return () => {
			window.removeEventListener('resize', checkMenuFit);
		};
	}, [navRef?.current]);

	return (
		<>
			<InfiosHeader
				isMobile={isMobile}
				id="unsetGridHeader"
				style={{opacity: "0%"}}
				className={`${status ? "open" : ""} ${
					scrollPosition > 5 ? "collapsed" : ""
				}`}
				isCollapsed={isCollapsed}
				isInEditor={isInEditor}
				ref={containerRef}
				onMouseEnter={() => handleMouseLeave("")}
				onMouseLeave={() => handleMouseLeave(null)}
				blackLetter={blackLetter}
				contactButtonColor={contactButtonColor}
				contactButtonTextColor={contactButtonTextColor}
				contactButtonHoverColor={contactButtonHoverColor}
				contactButtonTextHoverColor={contactButtonTextHoverColor}
			>
				<InfiosHeaderContainer isMobile={isMobile} ref={headerRef}>
					{!hideLogo && (
						<InfiosHeaderLogo isMobile={isMobile} href={logoURL} target={logoTargetLink}>
							<LazyLoadImage
								className="image__upload"
								src={logoSrc} alt={altLogo}
								title={titleLogo}/>
						</InfiosHeaderLogo>
					)}
					<InfiosHeaderWrapper isMobile={isMobile} blackLetter={blackLetter}  style={{opacity: 0}}>
						{(metaNavigationLinks || showSearch || showLanguageNavigation) &&
							<InfiosHeaderMetaWrapper isMobile={isMobile}>
								{metaNavigationLinks && (
									<HeaderMetaNav isMobile={isMobile} content={metaNavigationLinks} blackLetter={blackLetter}/>
								)}
								{showLanguageNavigation &&
									<LanguageSelector {...props} scrollPosition={scrollPosition} blackLetter={blackLetter}/>}
								{showSearch &&
									<SearchNavigation isMobile={isMobile} blackLetter={blackLetter} iconVersion showSuggestions
																		showPopular color={colors.white} {...props} />}
							</InfiosHeaderMetaWrapper>}
						<div className="header-navigation" ref={navRef}>
							<HeaderNavigation
								isMobile={isMobile}
								mouseLeave={mouseLeave}
								status={handleStatus}
								height={height}
								content={navigationLinks}
								blackLetter={blackLetter}
								backToText={backToText}
								overviewNavText={overviewText}
								resultsPage={resultsPage}
								showSearch={showSearch}
								hoverColor={hoverColor}
							/>
							{buttonText && (
								<Button
									id="contact-form"
									className="contact-form"
									onClick={() => {
										if (contactUrl) {
											window.open(contactUrl, contactUrlTarget);
											return;
										}
										setIsOverlayFormOpen(true);
										window.location.href = "#contact-form";
										targetView("header-forms-open");
									}}
									icon={"play"}
									text={buttonText}
								/>
							)}
						</div>
					</InfiosHeaderWrapper>
				</InfiosHeaderContainer>
			</InfiosHeader>
			<ContactFormOverlay
				id="unsetGridFormOverlay"
				open={isOverlayFormOpen}
				close={() => setIsOverlayFormOpen(false)}
				captchaConfig={captchaConfig}
				pageLanguage={pageLanguage}
				{...props}
			/>
		</>
	);
};

const HeaderHandleErrors = props => {
	return (
		<ErrorBoundary fallback={props.isInEditor || urlHasParam('wcmmode=disabled') ?
			<div style={{minHeight: '50px', display: 'flex', alignItems: 'center', backgroundColor: '#ffe1db'}}><p>The header
				component is currently experiencing errors. Please try again later.</p></div> : <div></div>}>
			<Header {...props}/>
		</ErrorBoundary>
	)
}

export default HeaderHandleErrors;
